import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, ClipboardCheck, ExternalLink, MapPin, Users, X } from 'lucide-react';
import { getAllEvents } from '../../utils/api';
import { getBrotherData } from '../../utils/auth';
import BrotherEventForm from './BrotherEventForm';
import api from '../../utils/api';
import { EmptyState, Notice, Panel } from './EventUI';

const Container = styled.div`
  display: grid;
  gap: 12px;

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const EventCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.shadow.subtle};
  padding: 16px 18px;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const EventHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const EventTitle = styled.h3`
  color: #2d3748;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const EventDetails = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.875rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.25rem;
  }
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  ${(props) =>
    props.$variant === 'primary'
      ? `
    background-color: #276749;
    color: white;

    &:hover {
      opacity: .84;
    }
  `
      : `
    background-color: white;
    color: #20201e;
    border: 1px solid #deded8;

    &:hover {
      background-color: #cbd5e0;
    }
  `}

  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.875rem;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #4a5568;

  &:hover {
    color: #2d3748;
  }

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;


const EventList = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [error, setError] = useState(null);
  const [fratInfo, setFratInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const brother = getBrotherData();
  const navigate = useNavigate();

  const fetchFratInfo = useCallback(async () => {
    try {
      const response = await api.get(`/frats/${brother.frat}`);
      setFratInfo(response.data.data);
    } catch (error) {
      setError('Failed to load fraternity information');
    }
  }, [brother.frat]);

  useEffect(() => {
    fetchFratInfo();
    fetchEvents();
  }, [fetchFratInfo]);

  const fetchEvents = async () => {
    try {
      const response = await getAllEvents();
      setEvents(response.data);
    } catch (error) {
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (event) => {
    setSelectedEvent(event);
  };

  const handleCloseForm = () => {
    setSelectedEvent(null);
  };

  const getPublicUrl = (event) => {
    if (!fratInfo) return '';
    return `${window.location.origin}/${fratInfo._id}/rush/${event._id}`;
  };

  const getRusheeSubmissionsUrl = (event) => {
    if (!fratInfo) return '';
    return `/${fratInfo._id}/rush/${event._id}/rushee-submissions`;
  };

  const getBrotherSubmissionsUrl = (event) => {
    if (!fratInfo) return '';
    return `/${fratInfo._id}/rush/${event._id}/brother-submissions`;
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <Container>
      {error && <Notice $type="error">{error}</Notice>}
      {loading && <Panel><EmptyState>Loading events…</EmptyState></Panel>}
      {!loading && !error && events.length === 0 && <Panel><EmptyState><CalendarDays size={28} /><h3>No events yet</h3><p>Create an event to start collecting responses.</p></EmptyState></Panel>}

      {fratInfo &&
        events.map((event) => (
          <EventCard key={event._id}>
            <EventHeader>
              <div>
                <EventTitle>{event.name}</EventTitle>
                <EventDetails>
                  <div><MapPin size={13} /> {event.location || 'Location TBD'}</div>
                  <div><CalendarDays size={13} /> {formatDateTime(event.start)} – {formatDateTime(event.end)}</div>
                </EventDetails>
              </div>
              <ButtonGroup>
                <Button $variant="primary" onClick={() => handleOpenForm(event)}>
                  <ClipboardCheck size={14} /> Attendance
                </Button>
                <Button onClick={() => window.open(getPublicUrl(event), '_blank')}>
                  <ExternalLink size={14} /> Public form
                </Button>
                <Button onClick={() => navigate(getRusheeSubmissionsUrl(event))}>
                  <Users size={14} /> Rushees
                </Button>
                <Button onClick={() => navigate(getBrotherSubmissionsUrl(event))}>
                  Brothers
                </Button>
              </ButtonGroup>
            </EventHeader>
          </EventCard>
        ))}

      {selectedEvent && (
        <Modal>
          <ModalContent>
            <CloseButton aria-label="Close form" onClick={handleCloseForm}><X /></CloseButton>
            <BrotherEventForm eventId={selectedEvent._id} />
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default EventList;
